import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock } from "lucide-react";

// Target: 18 July 2026 at 13:00 BST (UTC+1) = 12:00:00 UTC
const TARGET = new Date("2026-07-18T12:00:00Z").getTime();

function getTimeLeft() {
    const now = Date.now();
    const diff = Math.max(TARGET - now, 0);
    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
    };
}

function pad(n) {
    return String(n).padStart(2, "0");
}

function TimeSegment({ value, label }) {
    const displayVal = pad(value);
    return (
        <div className="flex flex-col items-center min-w-[32px]">
            <AnimatePresence mode="popLayout">
                <motion.span
                    key={displayVal}
                    initial={{ y: 5, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -5, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-sm font-bold text-slate-900 tabular-nums"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                    {displayVal}
                </motion.span>
            </AnimatePresence>
            <span className="text-[9px] uppercase tracking-tighter text-slate-400 font-bold -mt-0.5">
                {label}
            </span>
        </div>
    );
}

export default function CountdownTimer() {
    const [time, setTime] = useState(getTimeLeft);

    useEffect(() => {
        const id = setInterval(() => setTime(getTimeLeft()), 1000);
        return () => clearInterval(id);
    }, []);

    const expired = Object.values(time).every((v) => v === 0);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-center gap-3 bg-white/75 backdrop-blur-md border border-slate-200 rounded-full px-4 py-2 shadow-sm pointer-events-auto w-full justify-between"
            style={{ fontFamily: "'Poppins', sans-serif" }}
        >
            {/* Left icon part - matches the avatar stack look but with a clock */}
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#d5ff15] flex items-center justify-center border-[3px] border-white shadow-sm">
                <Clock className="w-4 h-4 text-black" />
            </div>

            {/* Right content part - matches the text side of AttendeeStrip */}
            <div className="flex items-center gap-2">
                {expired ? (
                    <p className="text-sm font-bold text-[#0D9488]">🎉 We're Live!</p>
                ) : (
                    <div className="flex items-center gap-1.5">
                        <TimeSegment value={time.days} label="d" />
                        <span className="text-slate-300 font-bold mb-3">:</span>
                        <TimeSegment value={time.hours} label="h" />
                        <span className="text-slate-300 font-bold mb-3">:</span>
                        <TimeSegment value={time.minutes} label="m" />
                        <span className="text-slate-300 font-bold mb-3">:</span>
                        <TimeSegment value={time.seconds} label="s" />
                    </div>
                )}

                <div className="h-6 w-px bg-slate-200 mx-1" />

                <div className="text-left leading-tight">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-900">
                        Until Event
                    </p>
                    <p className="text-[9px] text-[#0D9488] font-bold uppercase tracking-widest">
                        18 July · 1pm
                    </p>
                </div>
            </div>
        </motion.div>
    );
}
